import React, { useEffect, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import {
  DOLLAR_SIGN,
  INPUT_EVENT,
  MATH_FIELD,
  USER_TYPE
} from 'utils/constants';
import { traverseNodes } from 'utils/latexRenderer';

import styles from './styles.module.scss';

const BlockEmbed = Quill.import('blots/block/embed');

class MathBlot extends BlockEmbed {
  static blotName = 'math';
  static tagName = 'span';
  static className = 'math-field-wrapper';

  static create(value) {
    const node = super.create();
    const mathField = document.createElement(MATH_FIELD);
    mathField.value = value;
    mathField.className = styles.inlineMath;
    node.appendChild(mathField);

    // Live update math content
    mathField.addEventListener(INPUT_EVENT, () => {
      node.dataset.value = mathField.value;
    });

    node.dataset.value = value;
    return node;
  }

  static value(node) {
    return node.dataset.value || '';
  }
}

Quill.register(MathBlot);

// ---------------------------------------

const EditorField = ({ initialValue = '', onChange, readOnly = false }) => {
  const quillRef = useRef(null);
  const editorRef = useRef(null);
  const mounted = useRef(false);

  // TODO: Make sure that math-field input is disabled when readOnly
  // TODO: Add a Add/Edit/Delete MathFields functionality

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    editorRef.current = editor;

    if (!mounted.current) {
      const normalizedValue = initialValue.replace(/\\n/g, '\n');

      // Only parse once
      const parts = normalizedValue.split(/(\$.*?\$)/g);
      editor.setText('');

      parts.forEach((part) => {
        if (!part) return;
        if (part.startsWith(DOLLAR_SIGN) && part.endsWith(DOLLAR_SIGN)) {
          const latex = part.slice(1, -1);
          const range = editor.getSelection(true);
          editor.insertEmbed(range?.index || editor.getLength(), 'math', latex);
        } else {
          editor.insertText(editor.getLength(), part);
        }
      });

      mounted.current = true;
    }
  }, [initialValue]);

  const handleChange = (content, delta, source) => {
    if (source !== USER_TYPE) return;

    const editor = editorRef.current;
    if (!editor || !editor.root) return;

    const updated = traverseNodes({ node: editor.root });
    onChange?.(updated);
  };

  return (
    <ReactQuill
      ref={quillRef}
      readOnly={readOnly}
      theme={readOnly ? 'bubble' : 'snow'}
      onChange={handleChange}
      modules={{
        toolbar: readOnly
          ? false
          : [
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['clean'],
              [{ formula: [] }]
            ]
      }}
      placeholder="Type text with markup and math..."
    />
  );
};

export default EditorField;
